// React
import { Dispatch, SetStateAction, useState } from 'react';

// Refine
import { useNotification, useTranslation } from '@refinedev/core';

// Mantine
import {
  Button,
  Flex,
  Select,
  Stack,
  Alert,
  useComputedColorScheme,
} from '@mantine/core';

// Icons
import { IconInfoCircle, IconFilePlus } from '@tabler/icons-react';

// API
import { generateReportV2 } from 'api/action';

// Custom Components
import { DatePickerWithPresets } from 'components/common/DatePickerWithPresets';

// Constants
import { IncomeReportHeaders } from 'constants/reportHeaderConstants';

// Interfaces
import { ReportType } from 'interfaces';

// Zustand Stores
import { useSettingsStore } from 'store/SettingsStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

// Sentry
import * as Sentry from '@sentry/react';

// Utils
import { buttonTextColor } from 'utils/buttonUtils';

interface ProviderActivityReportModalContentProps {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  close?: () => void;
}

export const ProviderActivityReportModalContent: React.FC<
  ProviderActivityReportModalContentProps
> = ({ setIsLoading, isLoading, close }) => {
  const computedColorScheme = useComputedColorScheme();
  const { open } = useNotification();
  const { id: siteID } = useCurrentSiteStore();
  const { id: userID, cognitoID } = useCurrentUserStore();
  const { fiscalYearEnd } = useSettingsStore();
  const { translate } = useTranslation();

  // Report options
  const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
  const [dateType, setDateType] = useState<'serviceDate' | 'paymentDate'>(
    'serviceDate'
  );

  const handleCreateReport = async () => {
    try {
      setIsLoading(true);
      const reportPayload = {
        siteID,
        userID,
        cognitoID,
        startDate: dateRange[0],
        endDate: dateRange[1],
        headers: IncomeReportHeaders,
        dateType,
      };

      await generateReportV2(ReportType.providerIncome, reportPayload);
      open?.({
        message: translate('pages:reports.generateReport.inProgress'),
        type: 'success',
        key: 'success',
      });
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        message: translate('pages:reports.generateReport.error'),
        type: 'error',
        key: 'error',
      });
    } finally {
      setIsLoading(false);
      close();
    }
  };

  return (
    <Stack gap="md">
      <Alert
        color="yellow"
        variant="light"
        title="Info"
        icon={<IconInfoCircle />}
      >
        {translate('pages:reports.disclaimer')}
      </Alert>
      <DatePickerWithPresets
        dateRange={dateRange}
        setDateRange={setDateRange}
        fiscalYearEndMonth={fiscalYearEnd}
      />
      <Select
        label="Date Type"
        placeholder="Select date type"
        data={[
          { value: 'serviceDate', label: 'Service Date' },
          { value: 'paymentDate', label: 'Payment Date' },
        ]}
        value={dateType}
        onChange={(value) => {
          setDateType(value === 'paymentDate' ? 'paymentDate' : 'serviceDate');
        }}
        radius="lg"
        w="60%"
        mx="auto"
      />
      <Flex gap="sm" justify="center">
        <Button
          variant="outline"
          onClick={close}
          color="gray"
          c={buttonTextColor(computedColorScheme, isLoading)}
        >
          {translate('buttons.cancel')}
        </Button>
        <Button
          variant="outline"
          onClick={handleCreateReport}
          c={buttonTextColor(computedColorScheme, isLoading)}
          color="green"
          leftSection={<IconFilePlus />}
        >
          {translate('buttons.create')}
        </Button>
      </Flex>
    </Stack>
  );
};
